/// The app's windows, drawn onto 2D canvases in visionOS's own visual language.
///
/// The real headset app draws its hand keyboard exactly this way — Core Graphics into a texture on a mesh — so
/// this is not only the cheapest way to build a UI for three.js, it is the technique the thing being advertised
/// already uses.
///
/// **Matching visionOS matters more than looking nice here.** The point of the demo is "this is what it is
/// like", so the material is the real one: light frosted glass, white text, capsule controls with a soft white
/// fill, generous corner radii, and a grab bar with a close circle under anything that is a window rather than
/// an ornament. The room behind is kept bright for the same reason — visionOS glass takes its value from what
/// is behind it, and against a black void it would read as a dark card, which is not what anybody sees.
///
/// Everything here is a mock: no agent runs, nothing connects, and the text is written down below.

// The glass, the text and the little controls every picture is drawn with; the colours come from the tokens.
import { COLOR, RADIUS, TYPE, COMPONENT } from '../tokens.js'

// Every colour and dimension below comes from design/tokens.json. Nothing here is tuned by eye any more: if
// a value looks wrong against the app, the fix is in the JSON, and the Swift side reads the same file.
export const INK = COLOR.ink

export const DIM = COLOR.dim

export const FAINT = COLOR.faint

export const ACCENT = COLOR.orange

export const MINT = COLOR.mint

export const HOT = COLOR.tint

export const BLUE = COLOR.tint

export const GLASS = COLOR.glass

export const GLASS_EDGE = COLOR.glassEdge

export const CONTROL = COLOR.control

export const CONTROL_HI = COLOR.controlHi

export const SANS = TYPE.family

export const MONO = TYPE.mono

export function box(c, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2)
  c.beginPath()
  c.moveTo(x + radius, y)
  c.arcTo(x + w, y, x + w, y + h, radius)
  c.arcTo(x + w, y + h, x, y + h, radius)
  c.arcTo(x, y + h, x, y, radius)
  c.arcTo(x, y, x + w, y, radius)
  c.closePath()
}

export function fillBox(c, x, y, w, h, r, colour) {
  c.fillStyle = colour
  box(c, x, y, w, h, r)
  c.fill()
}

/// A capsule control with a label in it, which is most of visionOS's chrome.
export function capsule(c, x, y, w, h, label, { fill = CONTROL, colour = INK, size = 19, weight = 500, mono = false } = {}) {
  fillBox(c, x, y, w, h, h / 2, fill)
  if (label) {
    c.fillStyle = colour
    c.font = `${weight} ${size}px ${mono ? MONO : SANS}`
    c.textAlign = 'center'
    c.textBaseline = 'middle'
    c.fillText(label, x + w / 2, y + h / 2 + 1)
    c.textAlign = 'left'
  }
  return x + w
}

/// The pane of glass itself. `bar` adds the grab bar and close circle visionOS puts under a real window —
/// ornaments and attachments do not get one, which is why it is optional.
export function glass(c, w, h, { bar = false, radius = RADIUS.window } = {}) {
  c.clearRect(0, 0, w, h)
  const B = COMPONENT.window.bar
  bar = false   // the window bar is a real one under each window now (app.js addBar), not a picture of one in it
  const body = bar ? h - B.height : h
  fillBox(c, 1, 1, w - 2, body - 2, radius, GLASS)
  c.strokeStyle = GLASS_EDGE
  c.lineWidth = 2
  box(c, 1, 1, w - 2, body - 2, radius)
  c.stroke()

  if (bar) {
    // the close circle, then the grab bar, centred — exactly where visionOS puts them
    const y = body + 16
    fillBox(c, w / 2 + B.closeOffsetFromCentre, y, B.closeDiameter, B.closeDiameter, B.closeDiameter / 2, CONTROL)
    c.strokeStyle = 'rgba(255,255,255,0.75)'
    c.lineWidth = 2.5
    c.beginPath()
    c.moveTo(w / 2 - 121, y + 7); c.lineTo(w / 2 - 109, y + 19)
    c.moveTo(w / 2 - 109, y + 7); c.lineTo(w / 2 - 121, y + 19)
    c.stroke()
    fillBox(c, w / 2 - B.grabWidth / 2, y + 9, B.grabWidth, B.grabHeight, B.grabHeight / 2, CONTROL_HI)
  }
  return body
}

export function clip(c, text, max) {
  if (c.measureText(text).width <= max) return text
  let cut = text
  while (cut.length > 1 && c.measureText(cut + '…').width > max) cut = cut.slice(0, -1)
  return cut + '…'
}

export function wrap(c, text, x, y, max, lh, limit = 99) {
  const words = text.split(' ')
  let line = ''
  let lines = 0
  for (const word of words) {
    const next = line ? line + ' ' + word : word
    if (c.measureText(next).width > max && line) {
      c.fillText(line, x, y); y += lh; line = word
      if (++lines >= limit) return y
    } else line = next
  }
  if (line) { c.fillText(line, x, y); y += lh }
  return y
}

// Markdown blocks, as the headset's MarkdownBlocks draws them: a list is indented rows with the marker in a
// muted column, a quote has a bar down its left, a table is a rounded panel with a bold header row and a rule.
export function markdown(c, blocks, x, y, max) {
  for (const [kind, body] of blocks) {
    if (kind === 'list') {
      c.font = `400 15px ${SANS}`
      for (const row of body) {
        const depth = row.length - row.trimStart().length
        const [marker, ...rest] = row.trim().split(' ')
        c.fillStyle = 'rgba(255,255,255,0.55)'
        c.fillText(marker, x + depth * 7 + 6, y + 8)
        c.fillStyle = 'rgba(255,255,255,0.82)'
        y = wrap(c, rest.join(' '), x + depth * 7 + 24, y + 8, max - 24, 22)
      }
      y += 10
    } else if (kind === 'task') {
      // the Reminders look (MarkdownTheme.swift): a circle, or a filled green circle with a check, 22 pt
      c.font = `400 15px ${SANS}`
      for (const [done, text] of body) {
        const cx = x + 17, cy = y + 8
        if (done) {
          c.fillStyle = COLOR.green; c.beginPath(); c.arc(cx, cy, 10, 0, Math.PI * 2); c.fill()
          c.strokeStyle = '#fff'; c.lineWidth = 2.4; c.beginPath(); c.moveTo(cx - 5, cy); c.lineTo(cx - 1.5, cy + 3.5); c.lineTo(cx + 5.5, cy - 4); c.stroke()
        } else {
          c.strokeStyle = 'rgba(255,255,255,0.55)'; c.lineWidth = 1.6; c.beginPath(); c.arc(cx, cy, 10, 0, Math.PI * 2); c.stroke()
        }
        c.fillStyle = done ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.82)'
        y = wrap(c, text, x + 36, y + 8, max - 36, 22) + 6
      }
      y += 8
    } else if (kind === 'quote') {
      // a bar down the left; a nested quote is another bar, indented (MarkdownTheme.blockquote)
      const lines = typeof body === 'string' ? [[0, body]] : body   // [[depth, text], …]
      for (const [depth, text] of lines) {
        const ix = x + depth * 18
        for (let d = 0; d <= depth; d++) fillBox(c, x + 2 + d * 18, y, 3, 22, 1.5, `rgba(255,255,255,${0.45 - d * 0.1})`)
        c.fillStyle = `rgba(255,255,255,${0.6 - depth * 0.08})`
        c.font = `400 15px ${SANS}`
        y = wrap(c, text, ix + 16, y + 8, max - 16 - depth * 18, 22)
      }
      y += 10
    } else if (kind === 'table') {
      // a plain grid (MarkdownTable.swift): square cells, a rule under the header and between rows, the sorted
      // column marked, the whole width — no rounded box
      const cols = body[0].length
      const cw = (max - 8) / cols
      const rowH = 30
      body.forEach((row, i) => {
        const ry = y - 6 + i * rowH
        if (i === 2) fillBox(c, x, ry, cw * cols, rowH, 0, 'rgba(255,255,255,0.10)')   // the row being looked at
        c.fillStyle = i === 0 ? INK : 'rgba(255,255,255,0.82)'
        c.font = `${i === 0 ? 700 : 400} 14px ${SANS}`
        row.forEach((cell, j) => c.fillText(clip(c, cell + (i === 0 && j === 0 ? '  ▲' : ''), cw - 14), x + 10 + j * cw, y + 12 + i * rowH))
        fillBox(c, x, ry + rowH - 1, cw * cols, 1, 0, i === 0 ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.12)')
      })
      y += body.length * rowH + 14
    } else if (kind === 'rule') {
      fillBox(c, x, y + 4, max, 1, 0, 'rgba(255,255,255,0.18)')
      y += 16
    }
  }
  return y
}

/// The Copy / Change… / Open in a Pane row the app puts under a finished reply (MarkdownActions.swift).
export function actionRow(c, x, y) {
  for (const label of ['⧉ Copy', '✎ Change…', '▤ Open in a Pane']) {
    const aw = capsuleWidth(c, label, 12) + 22
    capsule(c, x, y, aw, 28, label, { size: 12 })
    x += aw + 6
  }
  return y + 40
}

/// A pane's header: a capsule with a glyph and a name, which is how the app labels and switches panes.
export function paneHeader(c, x, y, label) {
  const width = capsuleWidth(c, label, 15) + 30
  capsule(c, x, y - 17, width, 34, label, { size: 15 })
}

export function capsuleWidth(c, label, size) {
  c.font = `500 ${size}px ${SANS}`
  return Math.ceil(c.measureText(label).width)
}

export function paneLabel(c, text, x, y) {
  c.fillStyle = FAINT
  c.font = `600 13px ${SANS}`
  c.fillText(text, x, y)
}

/// The frame every session pane shares: title, path, the window buttons, and one header capsule.
/// How long ago, the way the Home window says it.
export function ago(ms) {
  if (!ms) return ''
  const s = Math.max(0, (Date.now() - ms) / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return Math.round(s / 60) + ' minutes ago'
  if (s < 86400) return Math.round(s / 3600) + ' hours ago'
  if (s < 172800) return 'yesterday'
  return Math.round(s / 86400) + ' days ago'
}

export function sessionFrame(c, w, h, paneLabel, live = null) {
  const body = glass(c, w, h, { bar: true })
  c.textBaseline = 'middle'
  c.fillStyle = INK
  c.font = `600 23px ${SANS}`
  c.fillText(clip(c, live ? live.title : 'Fix the quit hang', w - 220), 34, 44)
  c.fillStyle = DIM
  c.font = `400 15px ${SANS}`
  c.fillText(clip(c, live ? live.subtitle : '/tmp/cody-demo · big-pickle', w - 220), 34, 68)
  capsule(c, w - 170, 26, 44, 40, '⋯', { size: 18 })
  capsule(c, w - 118, 26, 44, 40, '❐', { size: 18 })
  capsule(c, w - 66, 26, 40, 40, '✕', { size: 17 })
  paneHeader(c, 34, 108, paneLabel)
  return body
}

/// A centred empty state — a glyph, a sentence, and capsules — which is how several panes look before the
/// agent has done anything. The real ones were captured like this; the populated versions below are what
/// they look like once it has.
export function emptyState(c, w, y, glyph, line, buttons = []) {
  c.textAlign = 'center'
  c.fillStyle = 'rgba(255,255,255,0.7)'
  c.font = `400 40px ${SANS}`
  c.fillText(glyph, w / 2, y)
  c.fillStyle = INK
  c.font = `600 21px ${SANS}`
  c.fillText(line, w / 2, y + 52)
  let by = y + 92
  for (const label of buttons) {
    const width = capsuleWidth(c, label, 17) + 40
    capsule(c, w / 2 - width / 2, by, width, 40, label, { size: 17 })
    by += 50
  }
  c.textAlign = 'left'
}
